import { createFileRoute, Outlet } from '@tanstack/react-router'
import { lazy, Suspense, useEffect, useState } from 'react'
import { useSuspenseQuery } from '@tanstack/react-query'
import { trpc } from '@/main'
import { prefetchJobChrome } from '@/hooks/use-job'
import { prefetchReport } from '@/hooks/use-report'

const JobDialog = lazy(() => import('./-components/jobs/job-dialog').then((m) => ({ default: m.JobDialog })))

/**
 * Layout for a selected job (`/jobs/$jobId/*`). This route renders real content (the incomplete-job
 * auto-open + the animated tab Outlet), so per AGENTS.md it OWNS the jobId-keyed prefetches that its
 * subtree reads — the persistent chrome (getJob + integrants list) and the report data shared by all
 * three tab children. The report/board/distribution leaves no longer duplicate these; they keep only
 * their own tab-specific queries (board/distribution: the integrant indicators).
 *
 * The `AnimatedOutlet` now lives in the parent `jobs.tsx` (wrapping the `<Outlet/>` that renders this
 * layout) so the enter animation plays once when the report panel first appears and never re-animates on
 * tab/job switches; this layout renders a plain `<Outlet/>` for its tab children. The auto-open's dismiss
 * state still persists across tab AND job switches because this chrome-owning layout stays mounted.
 */
export const Route = createFileRoute('/_app/jobs/$jobId')({
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps, params }) => {
    prefetchJobChrome(context.queryClient, deps, params)
    prefetchReport(context.queryClient, deps, params)
  },
  component: RouteComponent,
  // No pendingComponent: this passthrough layout just renders <AnimatedOutlet/>. The data-cold case is
  // covered by the parent jobs.tsx (<Suspense fallback={ReportPanelSkeleton}>), and each tab leaf
  // (report/board/distribution) owns its own tab-specific skeleton — a generic one here would mismatch.
})

function RouteComponent() {
  const { jobId } = Route.useParams()
  return (
    <>
      <Suspense fallback={null}>
        <IncompleteJobAutoOpen jobId={Number(jobId)} />
      </Suspense>
      <Outlet />
    </>
  )
}

/**
 * Auto-opens the completion dialog for an incomplete job the viewer owns. Reads `getJob` via suspense
 * (loader-prefetched above), inside its own <Suspense fallback={null}> so a cold read never blanks the
 * tab Outlet. Dismiss state is local to this layout — it persists across tab switches (the layout stays
 * mounted) and across job switches (same route, param change), and resets only when the selection is
 * cleared (navigating back to the `/jobs` list), which is the intended session scope.
 */
function IncompleteJobAutoOpen(props: { jobId: number }) {
  const { data: job } = useSuspenseQuery(trpc.jobs.getJob.queryOptions({ jobId: props.jobId }))
  const [dismissed, setDismissed] = useState<Set<number>>(() => new Set())

  // Once a job is complete it can never auto-open, so drop any stale dismiss flag (matters only if it
  // later becomes incomplete again).
  useEffect(() => {
    if (job.fullyRegistered) {
      setDismissed((prev) => {
        if (!prev.has(job.id)) return prev
        const next = new Set(prev)
        next.delete(job.id)
        return next
      })
    }
  }, [job])

  const shouldOpen = job.viewerRole === 'owner' && !job.fullyRegistered && !dismissed.has(job.id)

  // Mount the lazy dialog on first open and keep it mounted, driving visibility through `open` — a bare
  // `if (!shouldOpen) return null` would unmount it on dismiss and skip the exit animation.
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    if (shouldOpen) setMounted(true)
  }, [shouldOpen])

  if (!mounted) return null

  return (
    <JobDialog
      jobId={job.id}
      open={shouldOpen}
      onOpenChange={(open) => {
        if (!open) setDismissed((prev) => new Set(prev).add(job.id))
      }}
    >
      <span />
    </JobDialog>
  )
}
